import { useMemo } from "react"
import { useWidget } from "../../../providers/widget"
import { hostFontSizeCss } from "../../../common/host-scope"
import { AmenitiesWidgetConfig } from "../config"

/**
 * Dark-palette overrides for the auto FIRST-PAINT path (#900), mirroring the
 * reference explorer helper. The base template below always emits the LIGHT
 * palette (so light output is byte-identical); this appends the DARK palette
 * under two selector prefixes:
 *   - `:host([data-hr-theme="dark"])` — the runtime marker (explicit dark +
 *     post-hydration auto) and the mirrored elevated-dialog host.
 *   - inside `@media (prefers-color-scheme: dark)`,
 *     `:host([data-hr-scheme="auto"]:not([data-hr-theme]))` — a server-stamped
 *     auto widget on a dark OS, painting dark AT PARSE with no JS/cookie; the
 *     `:not` drops it the instant the runtime stamps the marker at hydration.
 *
 * The container/dialog `color` + dialog `background-color` reference the
 * `--foreground`/`--background` tokens, which common.css already flips under
 * both selectors — but the `.dark` CLASS they were keyed on is only applied at
 * hydration, so without these host-scoped siblings the amenity text falls back
 * to the UA default (black) at auto dark first paint. The accent is taken from
 * the DARK color set directly (not the resolvedTheme-gated `colors.accent`
 * used in the base) so it ships on every render, first paint included.
 *
 * CSS gotcha: BOTH conditions live INSIDE `:host(...)` —
 * `:host([data-hr-scheme="auto"]:not([data-hr-theme]))`, never
 * `:host([data-hr-scheme="auto"]):not([data-hr-theme])`, which silently never
 * matches the host.
 */
function amenitiesDarkOverrides(
  prefix: string,
  dc: AmenitiesWidgetConfig['darkModeColors'],
): string {
  return `
    ${prefix} .homerunner-amenities-container,
    ${prefix} .homerunner-amenities-dialog {
      color: var(--foreground) !important;
    }
    ${prefix} .homerunner-amenities-dialog {
      background-color: var(--background) !important;
    }
    ${dc.accent ? `
    ${prefix} .text-accent {
      color: ${dc.accent} !important;
    }
    ${prefix} .bg-accent {
      background-color: ${dc.accent} !important;
    }
    ${prefix} .border-accent {
      border-color: ${dc.accent} !important;
    }
    ` : ''}
  `
}

export function useAmenitiesCss(): string {
  const { options, resolvedTheme, widgetId } = useWidget<AmenitiesWidgetConfig>()

  const colors = useMemo(() => {
    return resolvedTheme === 'dark' ? options.darkModeColors : options.lightModeColors
  }, [resolvedTheme, options.lightModeColors, options.darkModeColors])

  return useMemo(() => `
    .homerunner-amenities-container *:not([class^=icon-]), .homerunner-amenities-dialog *:not([class^=icon-]) {
      font-family: '${options.font.family}' !important;
    }
    .homerunner-amenities-container.dark, .homerunner-amenities-dialog.dark {
      color: var(--foreground) !important;
    }
    .homerunner-amenities-dialog.dark {
      background-color: var(--background) !important;
    }
    ${hostFontSizeCss(widgetId, options.font.size)}
    ${colors.accent ? `
    .text-accent {
      color: ${colors.accent} !important;
    }
    .bg-accent {
      background-color: ${colors.accent} !important;
    }
    .border-accent {
      border-color: ${colors.accent} !important;
    }
    ` : ''}
    ${amenitiesDarkOverrides(':where(:host([data-hr-theme="dark"]))', options.darkModeColors)}
    @media (prefers-color-scheme: dark) {
      ${amenitiesDarkOverrides(':where(:host([data-hr-scheme="auto"]:not([data-hr-theme])))', options.darkModeColors)}
    }
    ${options.customCss ?? ''}
  `, [widgetId, options.font.family, options.font.size, colors.accent, options.darkModeColors.accent, options.customCss])
}
